import React, { useEffect, useState, useCallback, useMemo } from "react";
import moment from "moment";
import { useTranslation } from 'react-i18next';
import { useStoreState, useStoreActions } from 'easy-peasy';

import { Row, Col, Card, CardBody, CardHeader } from "stories/layout";
import { Input } from "stories/forms";
import { Typography, Button, Link, IconButton, Modal, Spinner, BreadcrumbsDynamic, Popover } from "stories/components";
import { PageContainer } from "components/Page"
import { TableAuto } from "stories/tables"
import { round, roundScaleDecimals, thousandFormat } from "services/helpers"

import { httpGetPortfolioStocksList, httpGetPortfolioSummary, httpGetPortfolioSummaryExcel } from "services/portfolios"
import { httpGetStockDateBalance } from "services/transactions"
import { httpGetStockData } from "services/stocks"
import { parseQuery } from "services/queryParams";
import CurrencySelect from "components/Forms/CurrencySelect/CurrencySelect"
import ReportCurrencyNote, { useReportCurrencyInfo } from "components/ReportCurrencyNote/ReportCurrencyNote"
import { reportCurrencyInfo, reportPortfolioInit, reportSelectedPortfolios } from "store/activePortfolios"
import { useNotification } from "stories/components/Notification"

import { useAppContext } from "contexts/AppContext"



import PortfoliosSummaryTableLineChart from "views/app/PortfoliosSummaryChart/PortfoliosSummaryTableLineChart" 
import { chartLiveMarketValue } from "views/app/PortfoliosSummaryChart/marketValueChartPoints"
import PortfolioSummaryPieChart from "views/app/PortfoliosSummaryChart/PortfolioSummaryPieChart" 
import PortfolioSummarySectorPieChart from "views/app/PortfoliosSummaryChart/PortfolioSummarySectorPieChart" 
import PortfolioSummaryTypePieChart from "views/app/PortfoliosSummaryChart/PortfolioSummaryTypePieChart" 

import PortfolioSelectStore from "components/Forms/PortfolioSelect/PortfolioSelectStore"
import PortfolioSelect from "components/Forms/PortfolioSelect/PortfolioSelect"
import SectorSelect from "components/Forms/SectorSelect/SectorSelect"
import TagSelect from "components/Forms/TagSelect/TagSelect"
import InvestmentTypeCateSelect from "components/Forms/InvestmentTypeCateSelect/InvestmentTypeCateSelect"
import CurrencyDateLogicSelect from "components/Forms/CurrencyDateLogicSelect/CurrencyDateLogicSelect"
import PIFUnlistedFundsSelect from "components/Forms/PIFUnlistedFundsSelect/PIFUnlistedFundsSelect"
import PieChartTypeSelect from "components/Forms/PieChartTypeSelect/PieChartTypeSelect"
import BestWorstPerformance from "views/app/BestWorstPerformance/BestWorstPerformance"


import PortfolioEdit from "views/app/PortfolioEdit/PortfolioEdit"
import { set } from "date-fns";
import { ka } from "date-fns/locale";
import { Pie } from "react-chartjs-2";

import { httpGetFairValues } from "services/fund_fair_values"
import {
  FAIR_VALUE_VIEWS,
  amountCell,
  fairValueHeaders,
  fairValueTotals,
  mergeFairValues,
  normalizeFairValuesResponse,
  sourceLabel,
  toFairValueParams,
} from "./fairValues"
import FairValueSource from "./FairValueSource"
import { formatAmount } from "views/app/FundValuationReportEdit/fundValuationPayload"

import "./PortfolioSummary.css";

const PortfolioSummary = ({match, history, location}) => {
  
  const { t } = useTranslation();
  const [summary, setSummary] = useState({
    market_value: 0.0,
    value_total_summary: 0.0,
    profit: 0.0,
    profit_percentage: 0.0,
  })
  const [stocks, setStocks] = useState()
  const [stocksData, setStocksData] = useState({})
  const [rows, setRows] = useState()
  const [portfolioSummaryLoading, setPortfolioSummaryLoading] = useState()
  const [selected_currency, setSelectedCurrency] = useState()
  const [selectedSector, setSelectedSector] = useState({sector_select: []});
  const [selectedTag, setSelectedTag] = useState({tag_select: []});
  const [checked, setChecked] = useState([])
  const [chartData, setChartData] = useState({})
  // Header market value of the latest successful summary request, { value, currency } or null.
  const [liveSummary, setLiveSummary] = useState(null)

  const { getSectorName, getSecurityTypeExtend  } = useAppContext()

  const account = useStoreState((state) => state.account?.account);

  // Fair values next to the market values, behind the account's Tableau flag
  // (server docs/fund-fair-value.md). fairValueBase is derived from the latest successful
  // summary request; the view is not persisted. fairValues: flat rows, null until loaded.
  const showFairValues = Boolean(account?.show_security_dim_fields)
  const [fairValueBase, setFairValueBase] = useState(null)
  const [fairValueView, setFairValueView] = useState("ifrs")
  const [fairValues, setFairValues] = useState(null)
  const [fairValueError, setFairValueError] = useState(false)

  useEffect(() => {
    if (!showFairValues || !fairValueBase) {
      setFairValues(null)
      setFairValueError(false)
      return undefined
    }
    // A late response of an older request (another view or summary) is dropped.
    let active = true
    setFairValues(null)
    setFairValueError(false)
    httpGetFairValues({ ...fairValueBase, view: fairValueView }).then((res) => {
      if (active) {
        setFairValues(normalizeFairValuesResponse(res?.data))
      }
    }, (error) => {
      if (active) {
        setFairValues([])
        // api.js already tells about a 403.
        setFairValueError(error?.status !== 403)
      }
    })
    return () => {
      active = false
    }
  }, [fairValueBase, fairValueView, showFairValues])


  const { notify } = useNotification();
  
  
  const portfolio = useStoreState((state) => state.portfolio?.portfolio);
  const portfolios = useStoreState((state) => state.portfolio?.portfolios);
  // The report's SALKKU filter starts from the active portfolios and follows them, not the
  // target portfolio, so choosing a target elsewhere does not fetch the report again (#1409).
  const activePortfolios = useStoreState((state) => state.portfolio?.activePortfolios);
  const activeKey = useStoreState((state) => state.portfolio?.activeKey);
  const loading = useStoreState((state) => state.portfolio?.loading);
  const count = useStoreState((state) => state.portfolio?.count);
  const getPortfolios = useStoreActions((actions) => actions.portfolio.getPortfolios);
  const setPortfolio = useStoreActions((actions) => actions.portfolio.setPortfolio);
  const setPortfolioSummary = useStoreActions((actions) => actions.portfolioSummary.setPortfolioSummary);
  const portfolioSummary = useStoreState((state) => state.portfolioSummary?.portfolioSummary);
  const portfolioSummaryPortfolios = useStoreState((state) => state.portfolioSummary?.portfolio);
  const setPortfolioSummaryPortfolios = useStoreActions((actions) => actions.portfolioSummary.setPortfolio);
  const [getPortfolioSummaryTable, setGetPortfolioSummaryTable] = useState(false)
  const [bestPerfomanceTimePeriod, setBestPerfomanceTimePeriod] = useState("month")
  const [lineChartTimePeriod, setLineChartTimePeriod] = useState(5)
  
  
  const [selectedPortfolios, setSelectedPortfolios] = useState(() => ({
    selected_portfolios: reportPortfolioInit(activePortfolios).selected_portfolios
  }))

  
  const [filters, setFilters] = useState({
    limit: 30,
    offset:0,
    date: moment().format("YYYY-MM-DD"),
    selected_portfolios: reportPortfolioInit(activePortfolios).selected_portfolios,
    ordering: 'name',
    investmentType:[],
    currency_date_logic: {value: "latest", label: t("latest", "Tilannepäivä")},
    calculate_type: [{"value":"value_total","label":"acq to zero based on total value"}],
    pie_chart_type: {value: 1, label: t("pie_chart_type_select_security", "Arvopaperi")},
  })

  const [unlisted_funds_use_own_logic, setUnlistedFundsUseOwnLogic] = useState(account?.pif_unlisted_funds_use_own_logic)

  useEffect(() => {
    setUnlistedFundsUseOwnLogic(account?.pif_unlisted_funds_use_own_logic)
  }, [account])


  useEffect(() => {
    const selectedPortfolioIds = [];
    let currency = portfolio?.currency_label



    // filters.selected_portfolios.forEach(item => selectedPortfolioIds.push(item.id));
    selectedPortfolios?.selected_portfolios.forEach(item => selectedPortfolioIds.push(item.id));

    if (selectedPortfolios?.selected_portfolios && selectedPortfolios?.selected_portfolios.length > 0){
      // The common currency of the selected portfolios, or EUR when they differ (#1409)
      currency = reportCurrencyInfo(reportSelectedPortfolios(selectedPortfolios.selected_portfolios, portfolios)).currency ?? undefined
    }
    setSelectedCurrency(currency)
    
    setSelectedPortfolios(s => ({
      ...s,
      portfolio: selectedPortfolioIds?selectedPortfolioIds:[portfolio?.id],
      selected_currency: currency
    }))
  }, [filters.selected_portfolios, selectedPortfolios?.selected_portfolios])

  useEffect(() => {
    const selectedSectors = [];
    selectedSector?.sector_select?.forEach(item => selectedSectors.push(item.id));
    setSelectedSector(s => ({
      ...s,
      sector: selectedSectors
    }))
  }, [selectedSector?.sector_select])

  useEffect(() => {
    const selectedTags = [];
    selectedTag?.tag_select?.forEach(item => selectedTags.push(item.id));
    setSelectedTag(s => ({
      ...s,
      tag: selectedTags
    }))
  }, [selectedTag?.tag_select])

  // useEffect(() => {
  //   if (account && account?.pif_unlisted_funds_use_own_logic) {
  //     setUnlistedFundsUseOwnLogic(account?.pif_unlisted_funds_use_own_logic)
  //   }
  // }, [account])







  // Excel export BEGIN
  
  const [loadingExport, setLoadingExport] = useState(false)

  const handleExcelExport = (file_type="excel") => {
    // many portfolios selected
    const put_together = selectedPortfolios?.portfolio?.length > 1 ? true : false

    let unlisted_funds_dividends = false
    let unlisted_funds_others = false
    let zero_based_on_total_value = false
    let zero_based_on_quantity = false
    let use_original_calculation = false


    if (filters?.calculate_type && filters?.calculate_type.length > 0){
      //loop all selected calculate types
      filters?.calculate_type.map((item, index) => {
        if (item.value == 30){
          unlisted_funds_dividends = true
        }
        if (item.value == 50){
          unlisted_funds_others = true
        }
        if (item.value == "value_total"){
          zero_based_on_total_value = true
        }
        if (item.value == "quantity"){
          zero_based_on_quantity = true
        }
        if (item.value == "use_original_calculation"){
          use_original_calculation = true
        }
      })
    }

    const params = {
      ...filters,
      date_transaction__date__gte: filters?.date_transaction__date__gte ? moment(filters?.date_transaction__date__gte).format("YYYY-MM-DD") : undefined,
      date_transaction__date__lte: filters?.date_transaction__date__lte ? moment(filters?.date_transaction__date__lte).format("YYYY-MM-DD") : undefined,
      stock_market: filters?.stock_market ? filters?.stock_market : undefined,
      // ordering: '-id',
      client_timezone_offset: Intl.DateTimeFormat().resolvedOptions().timeZone,
      export_excel: true,
      portfolio: selectedPortfolios?.portfolio,
      sectors: selectedSector?.sector,
      tags: selectedTag?.tag,
      date_transaction: filters?.date,
      selected_currency: selected_currency,
      put_together: put_together,
      for_investime: true,
      currency_date_logic: filters?.currency_date_logic?.value,
      file_type: file_type,
      unlisted_funds_use_own_logic: unlisted_funds_use_own_logic,
      unlisted_funds_dividends: unlisted_funds_dividends,
      unlisted_funds_others: unlisted_funds_others,
      zero_based_on_total_value: zero_based_on_total_value,
      zero_based_on_quantity: zero_based_on_quantity,
      use_original_calculation: use_original_calculation,
      // investmentType: filters?.investmentType?.map(item => item.id),
      investmentType: filters?.investmentType?.filter(item => item.id !== "all").map(item => item.id)
    }

    let fileName = "investime_portfolio_summary_export.xlsx";

    if (file_type === "pdf"){
      fileName = "investime_portfolio_summary_export.pdf";
    }
    
    let a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    
    setLoadingExport(true)

    if(file_type === "pdf"){
      notify({ title:t("Viedään PDF..."), icon: "fas fa-file-export"})
    }
    else{
      notify({ title:t("Viedään Exceliin..."), icon: "fas fa-file-export"})
    }


    httpGetPortfolioSummaryExcel(params).then(response => {
      let file = new Blob([response.data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});

      if (file_type === "pdf"){
        file = new Blob([response.data], { type: 'application/pdf' });
      }

      let fileURL = window.URL.createObjectURL(file);
      a.href = fileURL;
      a.download = fileName;
      a.click();
      notify({ title:t("Lataus valmis"), icon: "fas fa-file-export"})
    })
    .finally(() => {
      setLoadingExport(false)
    })
  }

  // portfolio_param: one portfolio, fetched as before. init: the SALKKU filter just set from
  // several active portfolios ({ portfolio: ids, currency }), before it is in state.
  const getSummaryData = (portfolio_param, init) => {
    //togle getPortfolioSummaryTable
    setGetPortfolioSummaryTable(!getPortfolioSummaryTable)

    const currency = init ? init.currency : selected_currency
    if(currency || portfolio_param){
      let unlisted_funds_dividends = false
      let unlisted_funds_others = false
      let zero_based_on_total_value = false
      let zero_based_on_quantity = false
      let use_original_calculation = false
  

  
      if (filters?.calculate_type && filters?.calculate_type.length > 0){
        //loop all selected calculate types
        filters?.calculate_type.map((item, index) => {
          if (item.value == 30){
            unlisted_funds_dividends = true
          }
          if (item.value == 50){
            unlisted_funds_others = true
          }
          if (item.value == "value_total"){
            zero_based_on_total_value = true
          }
          if (item.value == "quantity"){
            zero_based_on_quantity = true
          }
          if (item.value == "use_original_calculation"){
            use_original_calculation = true
          }

        })
      }


      const params = {
        // 'portfolio': portfolio?.id,
        'portfolio': portfolio_param? [portfolio_param.id]: init ? init.portfolio : selectedPortfolios?.portfolio, // many portfolios can be selected
        "put_together": true,
        'date_transaction': filters?.date,
        'selected_currency': portfolio_param? portfolio_param?.currency_label: currency,
        'sectors': selectedSector?.sector,
        'tags': selectedTag?.tag,
        // 'investmentType': filters?.investmentType?.map(item => item.id),
        'investmentType': filters?.investmentType?.filter(item => item.id !== "all").map(item => item.id),
        'ordering_by': filters?.ordering,
        'for_investime': true,
        'currency_date_logic': filters?.currency_date_logic?.value,  // 'latest' or 'historical'
        'unlisted_funds_use_own_logic':  unlisted_funds_use_own_logic,
        'use_original_calculation': use_original_calculation,
        'unlisted_funds_dividends': unlisted_funds_dividends,
        'unlisted_funds_others': unlisted_funds_others,
        'zero_based_on_total_value': zero_based_on_total_value,
        'zero_based_on_quantity': zero_based_on_quantity,

      
        // 'ordering': 'related_model', // NOT WORK....
      }

      const hasPortfolios = init ? init.portfolio.length > 0 : (selectedPortfolios?.selected_portfolios?.length > 0 && selectedPortfolios?.portfolio?.length > 0)
      if (hasPortfolios || portfolio_param) {
        setPortfolioSummaryLoading(true)
        setLiveSummary(null)

        httpGetPortfolioSummary(params).then(res => {
          if (res?.data) {
            //order by related_model.name
            // res.data.sort((a, b) => (a.related_model.name > b.related_model.name) ? 1 : -1)
            setRows(res?.data)
            setFairValueBase(toFairValueParams(params))

            let market_value = 0.0
            let value_total_summary = 0.0
            // loop all res.datas and calculate market_value
            res.data.map((item, index) => {
              if (item?.related_model_type == "10") {
              // market_value += item?.balance * item?.latest_stock_data?.ending_price
              market_value += item?.market_value
              }
              else {
                // market_value += item?.balance * item?.latest_stock_data?.price
                market_value += item?.market_value
              }
              value_total_summary += item?.value_total_summary
            })
            setSummary(s => ({
              ...s,
              market_value: market_value,
              value_total_summary: value_total_summary,
            }))
            setLiveSummary({ value: market_value, currency: params.selected_currency })
            if (portfolio_param && res?.data.length > 0){

              //do not do this if page are reloaded
              if (res?.data[0]?.portfolio == portfolio?.id){

                setPortfolioSummary(res?.data)
                setPortfolioSummaryPortfolios({market_value: market_value, value_total_summary: value_total_summary})
              }
            }


            setPortfolioSummaryLoading(false)
          } 
          else  
          {
            setRows([])
            setPortfolioSummary([])
            setPortfolioSummaryPortfolios({market_value: 0.0, value_total_summary: 0.0})
            setLiveSummary(null)

            setPortfolioSummaryLoading(false)
            setSummary(s => ({  
              ...s,
              market_value: 0.0,
              value_total_summary: 0.0,
            }))

          } 
        }, (error) => {
          setRows([])
          setPortfolioSummary([])
          setPortfolioSummaryPortfolios({market_value: 0.0, value_total_summary: 0.0})
          setLiveSummary(null)

          setPortfolioSummaryLoading(false)
            setSummary(s => ({  
              ...s,
              market_value: 0.0,
              value_total_summary: 0.0,
            }))
        }
        )
      }
  }
  }


  useEffect(() => {
    const init = reportPortfolioInit(activePortfolios)
    setSelectedCurrency(init.currency ?? undefined)
    setSelectedPortfolios({selected_portfolios: init.selected_portfolios})

    if (activePortfolios.length > 1) {
      getSummaryData(null, init)
    }
    else if ((portfolioSummary?.length <= 0 && (portfolioSummaryLoading == undefined || !portfolioSummaryLoading )) || (portfolioSummary?.length > 0 && Number(PortfolioSummary[0]?.portfolio) !== Number(activePortfolios[0]?.id ))) {
      // if (portfolioSummary?.length <= 0 ) {
      // console.log("portfolio.id", portfolio.id, portfolioSummary[0]?.portfolio)
      getSummaryData(activePortfolios[0])
    }
    // setGetPortfolioSummaryTable(!getPortfolioSummaryTable)
    // else{

    //   setSummary(s => ({  
    //     ...s,
    //     market_value: portfolioSummaryPortfolios?.market_value,
    //     value_total_summary: portfolioSummaryPortfolios?.value_total_summary,
    //   }))
    // }
    // console.log("portfolio.id", portfolio.id)
  }, [activeKey])

    
  
  
  const getCrumbLinks = () => {
    const crumbLinks = [
      {name: t("Etusivu"), url: "/dashboard/"},
      {name: t("portfolios_list_title","Salkut"), url: "/dashboard/portfolios"},

      {name: t("portfolios_summary_path","Salkun yhteenveto"), url: "/dashboard/portfolios/summary", optional: true},
      {name: portfolio?.name, url: "/dashboard/portfolios/summary", optional: true},

    ]

    //if location state include crumbLinks return them
    if (location?.state && location?.state?.crumbLinks) {
      const crumbLinks = [...location.state.crumbLinks]
      crumbLinks.push({name: portfolio?.name, url: location.pathname})
      return crumbLinks
    }
    //else return default links
    return crumbLinks
  }

  const handleChange = ({target}) => {
    setFilters({
      ...filters,
      [target.name]: target.value,
    })
  }
  
  const paginationPrevious = () => {
    let offset = filters?.offset - filters?.limit;
    if (offset < 0) {
      offset = 0;
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }
  
  const paginationNext = () => {
    let offset = filters?.offset + filters?.limit;
    if (offset > filters?.count) {
      return
    }
    setFilters(f => ({
      ...f,
      offset: offset,
    }))
  }
  
  const headers = [
    { label: t("portfolios_list_name", "Arvopaperi"), key: "name" , sortable: false,  sortingKey: "name" },
    { label: t("portfolios_list_balance", "Salkussa"), key: "balance"},
    { label: t("portfolios_list_balance_%", "Omistus%"), key: "size"},
    { label: t("portfolios_list_purchase_avg", "Hankinta-kurssi*"), key: "purchase_price_weighted_avg"},
    { label: t("portfolios_list_value_total_summary", "Hankinta-meno"), key: "value_total_summary"},
    { label: t("portfolios_list_category", "Laji"), key: "category"},
    { label: t("portfolios_list_secotor", "Toimiala"), key: "sector"},
    { label: t("portfolios_list_rate", "Markkina-kurssi"), key: "rate"},
    { label: t("portfolios_list_latest_stock_data_date", "Viimeisin kurssi-kirjaus"), key: "latest_stock_data_date"},

    { label: t("portfolios_list_value", "Markkina-arvo"), key: "market_value"},
    { label: `${t("portfolios_list_value_change", `Muutos`)} ${selected_currency}`, key: "market_value_change"},
    { label: t("portfolios_list_value_change_percent", `Muutos %`), key: "market_value_change_percent"},
    // { label: t("portfolios_list_expenses", "Hankintameno"), key: "expenses"},
    // { label: t("portfolios_list_profit", "Tuotto"), key: "profit"},
    // { label: t("portfolios_list_change", "Muutos"), key: "change"},
    // { label: t("portfolios_list_change_eur", "Muutos EUR"), key: "change_eur"},
    // { label: t("portfolios_list_other_profit_expenses", "Muut tuotot ja kulut"), key: "other_profit_expenses"},
    { label: t("portfolios_list_actions", " "), key: "actions"},
  ];

  const get_headers = useCallback(() => {

    // account?.show_relation_to_security && headers.splice(1, 0, { label: t("PrivateInvestmentFundRatesList_stock", "Kirjanpidoss"), key: "accounting_price_boolean"})

    const modifiedHeaders = [...headers]; // Create a copy of the headers array

    if (account?.show_relation_to_security) {
      modifiedHeaders.splice(7, 0, { label: t("portfolio_summary_accounting_price", "Kirjanpito-arvo"), key: "accounting_price" });
      modifiedHeaders.splice(10, 0, { label: t("portfolio_summary_accounting_market_value", "Kirjanpidon mark. arvo"), key: "accounting_market_value" });
    }

    // After the fixed-index splices above: the fair value columns follow market_value by key.
    if (showFairValues) {
      return fairValueHeaders(modifiedHeaders, t);
    }

    return modifiedHeaders;
  }, [account, selected_currency, showFairValues])

  // The summary rows with their fair value (null while the flag is off).
  const fairValueRows = useMemo(() => {
    if (!showFairValues) {
      return null
    }
    return mergeFairValues(rows, fairValues, fairValueBase?.portfolio)
  }, [showFairValues, rows, fairValues, fairValueBase])

  const fairValueTotal = useMemo(
    () => (fairValueRows && fairValues && !fairValueError ? fairValueTotals(fairValueRows) : null),
    [fairValueRows, fairValues, fairValueError]
  )

  // Cells of one row; a missing value is the string "–", never { content: "" }.
  const fairValueCells = (fairValue, index) => {
    const reports = fairValue?.reports || []
    return {
      fair_value: amountCell(fairValue?.fair_value),
      fair_value_difference: amountCell(fairValue?.difference),
      fair_value_source: reports.length > 0
        ? <FairValueSource id={`fv-src-${index}`} sources={fairValue.sources} reports={reports} />
        : sourceLabel(fairValue?.sources, t),
    }
  }
  
  // useEffect(() => {
  //   let _rows = []
  //   let _summary = {
  //     market_value: 0.0
  //   }
  //   stocks && stocks.map((stock, index) => {
  //     const obj = {
  //       balance: 0,
  //       rate: 0,
  //       ...stocksData[stock.id]
  //     }
      
  //     const market_value = round(obj?.balance * obj?.rate, 2)
      
  //     // Row
  //     _rows.push({
  //       ...obj,
  //       market_value: market_value
  //     })
      
  //     // Summary
  //     _summary.market_value = round(_summary.market_value + market_value, 2)
  //   })
  //   setRows(_rows)
  //   setSummary(_summary)
  // }, [stocksData])
  
  const getTableRows = useCallback(() => {
    let rows_data = rows

    // if (portfolioSummary?.length > 0) {
    //   rows_data = portfolioSummary
    // }

    return rows_data && rows_data.length > 0 && rows_data.map((row, index) => {
      let rate = row?.latest_stock_data?.price
      let accounting_rate = row?.latest_accounting_stock_data?.price
      let latest_stock_data_date = row?.latest_stock_data?.date
      if (row.latest_stock_data?.stock_date){
        latest_stock_data_date = row.latest_stock_data?.stock_date
      }

      if (row?.related_model_type == "10") {
        rate = row?.latest_stock_data?.ending_price
      }
      if (row?.related_model_type != "30") {
        accounting_rate = rate
      }
      let market_value = row?.market_value
      
      let market_value_change = market_value + row?.value_total_summary

      let market_value_change_percent = roundScaleDecimals(market_value_change / (row.value_total_summary * -1)* 100, 2)
 
      if (isNaN(Number(market_value_change_percent)) ) {
        market_value_change_percent = roundScaleDecimals(1-1, 2) // 0.00
      }

      const size = row?.related_model?.size;

      const calculatePercentage = (balance, size) => {
        const percentage = (balance / size) * 100;
        return percentage > 0.0001 ? roundScaleDecimals(percentage, 4) : percentage;
      };
      let related_model_type_cell = (
        <span className="text-start text-dark m-0" style={{maxWidth:"10px"}}>
          <div id={`issue-${index}`}>{t("Alle 0,0001")}%</div>
          <Popover
            placement="top"
            target={`issue-${index}`}
          >       
            <Typography className="text-dark mb-1" variant="medium">
              {calculatePercentage(row?.balance, Number(size))} 
            </Typography>  
          </Popover>
        </span> 
      )

      const result = size !== null && size !== undefined && size !== 0
        ? calculatePercentage(row?.balance, Number(size)) > 0.0001 ? `${calculatePercentage(row?.balance, Number(size))}%` : related_model_type_cell
        : t("Ei saatavilla");


        // Crypto quantities are fractional (e.g. 0.0005 BTC) — always 6 decimals, regardless of
        // the account flag. 6 matches the backend PortfolioSummaryTable.balance precision.
        const balance = row?.related_model?.type === "crypto" ? {
          content: `${thousandFormat(roundScaleDecimals(row?.balance, 6))}`,
          className: 'text-right'
        }: (account?.portfolio_summary_quantity_decimals_logic == true && ["10", "private_stock", "listed_stock"].includes(row?.related_model?.type))? {
          content: `${thousandFormat(roundScaleDecimals(row?.balance, 0))}`,
          className: 'text-right'
        }: account?.portfolio_summary_quantity_decimals_logic == true ?{
          content: `${thousandFormat(roundScaleDecimals(row?.balance, 4))}`,
          className: 'text-right'
        }: 
        {
          content: `${thousandFormat(roundScaleDecimals(row?.balance, 2))}`,
          className: 'text-right'
        }

        
  
      
      return {
        ...row,
        id: row?.related_model?.id,
        name: {
          content: <strong>{row?.related_model?.name}</strong>,
        },
        sector: getSectorName(row?.related_model?.sector_code) ? t(`sector_select_${row?.related_model?.sector_code}`,getSectorName(row?.related_model?.sector_code)) : "",
        category: t(row?.related_model?.type, getSecurityTypeExtend(row?.related_model_type, row?.related_model?.type)?.value),
        size: result,
        // balance: `${roundScaleDecimals(row?.balance, 2)} ${t("kpl")}`,
        // balance: {'
        //   content: `${thousandFormat(roundScaleDecimals(row?.balance, 2))}`,
        //   className: 'text-right'
        // },'
        balance: balance,
        rate: rate?`${roundScaleDecimals(rate, 2)}`:<span className="text-danger">{t("Ei saatavilla")}</span>,
        // purchase_price_weighted_avg: `${roundScaleDecimals(row?.purchase_price_weighted_avg, 2)} ${portfolio?.currency_label}`,
        purchase_price_weighted_avg: {
          content: `${thousandFormat(roundScaleDecimals(row?.purchase_price_weighted_avg * -1, 2))}`,
          className: 'text-right'
        },
        accounting_price: accounting_rate?`${roundScaleDecimals(accounting_rate, 2)}`:<span className="text-danger">{t("Ei saatavilla")}</span>,

        latest_stock_data_date: latest_stock_data_date ?moment(latest_stock_data_date).format("DD-MM-YYYY"): <span className="text-danger">{t("Ei saatavilla")}</span>  ,
        // value_total_summary: `${roundScaleDecimals(row?.value_total_summary,2)}`,
        value_total_summary: {
          content: `${thousandFormat(roundScaleDecimals(row?.value_total_summary, 2))}`,
          className: 'text-right'
        },
        // market_value: `${roundScaleDecimals(market_value, 2)} ${portfolio?.currency_label}`,
        market_value: {
          content: `${thousandFormat(roundScaleDecimals(market_value, 2))} `,
          className: 'text-right'
        },
        accounting_market_value: {
          content: `${thousandFormat(roundScaleDecimals(row?.accounting_market_value, 2))} `,
          className: 'text-right'
        },

        // market_value_change: `${roundScaleDecimals(market_value_change, 2)} ${portfolio?.currency_label}`,
        market_value_change: {
          content: `${thousandFormat(roundScaleDecimals(market_value_change, 2))}`,
          className: 'text-right'
        },
        // market_value_change_percent: `${roundScaleDecimals(market_value_change / (row.value_total_summary * -1)* 100, 2)} %`,
        market_value_change_percent: {
          content: `${market_value_change_percent} %`,
          className: 'text-right'
        },

        ...(fairValueRows ? fairValueCells(fairValueRows[index]?.fairValue, index) : {}),
      }
    })
  }, [rows, portfolioSummary, fairValueRows])
  
  // Modal: New/Edit portfolio
  
  const [modalEditOpen, setModalEditOpen] = useState(false)
  const [modalEditId, setModalEditId] = useState()
  
  const toggleModalEdit = () => {
    setModalEditOpen(s => !s);
  }
  
  const openModalEdit = (id) => {
    setModalEditOpen(true)
    setModalEditId(id);
  }
  
  const modalEditResolve = () => {
    setModalEditOpen(false);
    setModalEditId(null);
    getPortfolios()
  }
  
  const modalEditReject = () => {
    setModalEditOpen(false);
    setModalEditId(null);
  }

  const setFieldValue = (name, value) => {
    setFilters(s => ({
      ...s,
      [name]: value
    }))
  }

  const handlePortfolioChange = (name, value) => {
    setSelectedPortfolios(s => ({
      ...s,
      [name]: value
    }))
  }

  const handlePortfolioChange1= ({target}) => {
    if (target.value !== "10" ) {
      setFilters(s => ({
        ...s,
        [target?.name]: target.value,
        related_model_type: undefined,
        stock_market: undefined,
        related_model_id: undefined
      }))

    }
    else {
    setFilters(s => ({
      ...s,
      [target?.name]: target.value,
      related_model_type: undefined,
      related_model_id: undefined
    }))
  }
  }

  const handleSectorChange = (name, value) => {
    setSelectedSector(s => ({
      ...s,
      [name]: value
    }))
  }
  const handleTagChange = (name, value) => {
    setSelectedTag(s => ({
      ...s,
      [name]: value
    }))
  }

  // EUR, USD, SEK and the currencies of every portfolio in the SALKKU filter (#1409)
  const { currencyOptions } = useReportCurrencyInfo(selectedPortfolios?.selected_portfolios)

  const getCurrencySelect = useCallback(() => {
    let default_currencys = currencyOptions

    //ADD ALL PORTFOLIO CURRENCYS TO DEFAULT CURRENCYS
    // //map all portfolios and get all currencys
    // const all_currencys = portfolios.map((portfolio) => {
    //   return portfolio?.currency_label
    // })
    // //remove duplicates
    // const unique_currencys = [...new Set(all_currencys)];
    // //add unique currencys to default currencys
    // default_currencys = [...default_currencys, ...unique_currencys]

    return (
      <CurrencySelect 
      name='currency'
      value={selected_currency}
      onChange={(event) => setSelectedCurrency(event.target.value)}
      label={t("Valuutta")} 
      disabled={false}
      useOnlyList = {default_currencys}
      // useOnlyList = {filters?.selected_currency}
      />
    )
  }, [selected_currency, currencyOptions])

  const getPortfolioSelect = useCallback(() => {
    return (
      <PortfolioSelect 
      name="selected_portfolios"
      label={t("Salkku")}
      setFieldValue={handlePortfolioChange}
      multiple={true}
      placeholder={t("select_portfolio", "Valitse salkku")}
      onChange={handlePortfolioChange}
      value= {selectedPortfolios?.selected_portfolios}
    />
    )
  }, [portfolio, selectedPortfolios?.selected_portfolios, selectedPortfolios])

  const getSectorSelect = useCallback(() => {
    return (
      <SectorSelect
        name="sector_select"
        label={`${t("sector_title", "Toimiala")}` }
        multiple={true}
        onChange={handleSectorChange}
        value={selectedSector?.sector_select}
        setFieldValue={handleSectorChange}
      />
    )
  }, [selectedSector])

  const getTagSelect = useCallback(() => {
    return (
      <TagSelect
        name="tag_select"
        label={`${t("tag_title", "Kategoria")}` }
        multiple={true}
        onChange={handleTagChange}
        value={selectedTag?.tag_select}
        setFieldValue={handleTagChange}
      />
    )
  }, [selectedTag])

  const getFirstDayXMonthsAgo = useCallback((months, endDate) => {

    let date = new Date();
    if (endDate) {
      date = new Date(endDate);
    }
    // console.log("date", date)
    // Vähennä 6 kuukautta nykyisestä kuukaudesta
    date.setMonth(date.getMonth() - months);
  
    // Aseta päivä kuukauden ensimmäiseksi päiväksi
    date.setDate(1);
  
    return date.toISOString().split('T')[0];
  }, [filters?.date])

  

  const getTable = useCallback(() => {
    return(
      <>
      <TableAuto
      showRowNumber={false}
      showId={false}
      checkboxes={false}
      headers={get_headers()}
      headerColor={"light"}
      rows={getTableRows()}
      loading={portfolioSummaryLoading}
      pagination={true}
      paginationPrevious={paginationPrevious}
      paginationNext={paginationNext}
      paginationOffset={filters?.offset}
      paginationLimit={filters?.limit}
      paginationCount={count}
      setFilters={setFilters}
      setChecked={setChecked}
      name="portfolio_summary_table"
      footerActions={
        <div className="sk-table-footer-actions d-flex flex-wrap align-items-center">
          <Button className="mr-2" color="secondary" size="sm" disabled={loadingExport} onClick={() => handleExcelExport("excel")}>
            <i className="fas fa-file-excel"></i>&nbsp;
            {t("excel_export", "Vie (Excel)")}
          </Button>
          <Button color="secondary" size="sm" disabled={loadingExport} onClick={() => handleExcelExport("pdf")}>
            <i className="fas fa-file-pdf"></i>&nbsp;
            {t("pdf_export", "Vie (PDF)")}
          </Button>
        </div>
      }
      />
      </>
    )
    },[portfolioSummaryLoading, loadingExport, count, filters?.offset, filters?.limit, t, showFairValues, fairValueRows])



  const getInvestmentTypeSelect = useCallback(() => {
    return <InvestmentTypeCateSelect name="investmentType" onChange={setFieldValue} showSubsOptions={true} showAllOption={false}  multiple={true} setFieldValue={setFieldValue} value={filters.investmentType} label={t("investmentType_select_label",'Arvopaperi laji')} />
    }, [filters?.investmentType,])

  const getCurrencyDateLogicSelect = useCallback(() => {
    return <CurrencyDateLogicSelect  name="currency_date_logic" onChange={setFieldValue} setFieldValue={setFieldValue} value={filters?.currency_date_logic} />
    }, [filters?.currency_date_logic,])


    const [aspectRatio, setAspectRatio] = useState(10 / 4); // Default aspect ratio

  // Handle window resize to adjust aspect ratio based on column size
  useEffect(() => {
    const handleResize = () => {
      // Check the window width and set the aspect ratio
      if (window.innerWidth >= 1500) { // xl size (screen width >= 1200px)
        setAspectRatio(10/6); // Aspect ratio for xl screen size
      } else if (window.innerWidth >= 1200) { // md size (screen width >= 768px)
        setAspectRatio(10 / 2); // Aspect ratio for md screen size
      } else if (window.innerWidth >= 992) { // md size (screen width >= 768px)
        setAspectRatio(10 / 3); // Aspect ratio for md screen size
      } else if (window.innerWidth >= 768) { // md size (screen width >= 768px)
        setAspectRatio(10 / 4); // Aspect ratio for md screen size
      } else {
        setAspectRatio(10 / 6); // Default for smaller screens
      }
    };

    // Call the handler initially and whenever the window is resized
    handleResize();
    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array to run only once

  const chartPortfolioList = useMemo(
    () => selectedPortfolios?.portfolio || (portfolio?.id ? [portfolio.id] : []),
    [selectedPortfolios?.portfolio, portfolio?.id]
  );
  
  const get_charts = () => {
    return (
      <>
    {(account?.show_frontpage_portfolio_summary ) &&
      <Row className="mb-3">
        <Col xs={12} xl={4} className="mb-3 mb-xl-0">
          <Card className="portfolio-summary-chart-card shadow-sm-strong h-100">
          <CardHeader className="sk-frontpage-card-header portfolio-summary-chart-card__header d-flex justify-content-between align-items-start flex-wrap">
              <div className="d-flex align-items-start mr-2 mb-2 mb-lg-0 flex-grow-1 min-w-0">
                <div className="sk-card-header-icon-box mr-3 flex-shrink-0" aria-hidden>
                  <i className="fas fa-chart-area text-muted" />
                </div>
                <div className="min-w-0">
                  <div className="portfolio-summary-chart-card__eyebrow text-muted text-uppercase small font-weight-bold">
                    {t("market_value","Markkina-arvo")}
                  </div>
                  <div className="portfolio-summary-chart-card__value-row">
                    {summary?.market_value != null
                      ? `${thousandFormat(roundScaleDecimals(summary.market_value, 2))} ${selected_currency || portfolio?.currency_label || chartData?.currency || ""}`
                      : chartData?.latest_market_value
                        ? `${thousandFormat(chartData.latest_market_value.toFixed(2))} ${chartData?.currency ? chartData.currency : selected_currency || portfolio?.currency_label}`
                        : ""}
                  </div>
                </div>
              </div>
              <div className="portfolio-summary-segmented align-self-start ml-md-auto flex-shrink-0 mt-2 mt-lg-0">
                  <button
                    type="button"
                    className={`portfolio-summary-segmented__btn ${lineChartTimePeriod === 5 ? "is-active" : ""}`}
                    onClick={() => setLineChartTimePeriod(5)}
                  >
                    {t("line_chart_6_months","6kk")}
                  </button>
                  <button
                    type="button"
                    className={`portfolio-summary-segmented__btn ${lineChartTimePeriod === 12 ? "is-active" : ""}`}
                    onClick={() => setLineChartTimePeriod(12)}
                  >
                    {t("line_chart_1_year","1v")}
                  </button>
                  <button
                    type="button"
                    className={`portfolio-summary-segmented__btn ${lineChartTimePeriod === 36 ? "is-active" : ""}`}
                    onClick={() => setLineChartTimePeriod(36)}
                  >
                    {t("line_chart_3_year","3v")}
                  </button>
              </div>
          </CardHeader>
            <CardBody className="pt-2">
              {
                
                rows && rows.length > 0 &&
                <PortfoliosSummaryTableLineChart 
                portfolio_list={chartPortfolioList} 
                date__gte={getFirstDayXMonthsAgo(lineChartTimePeriod, filters?.date)} 
                date__lte={filters?.date} 
                related_model_type={filters?.investmentType} 
                sectors={selectedSector?.sector} 
                setChartData={setChartData} 
                rows={rows} 
                type={'market_value'} 
                currency={selected_currency}
                aspectRatio={aspectRatio}
                themeHostSelector=".portfolio-summary-chart-card"
                liveMarketValue={chartLiveMarketValue(liveSummary, { chartCurrency: selected_currency, tags: selectedTag?.tag })}
                statusDate={filters?.date}
                />
              }
            </CardBody>
          </Card>
        </Col>


        

        <Col xs={12} xl={4} className="mb-3 mb-xl-0">
          <Card className="portfolio-summary-chart-card shadow-sm-strong h-100">
            <CardHeader className="sk-frontpage-card-header portfolio-summary-chart-card__header d-flex justify-content-between align-items-start flex-wrap">
                <div className="d-flex align-items-start mr-2 mb-2 mb-lg-0 flex-grow-1 min-w-0">
                  <div className="sk-card-header-icon-box mr-3 flex-shrink-0" aria-hidden>
                    <i className="fas fa-chart-pie text-muted" />
                  </div>
                  <div className="min-w-0">
                    <div className="portfolio-summary-chart-card__eyebrow text-muted text-uppercase small font-weight-bold">
                      {t("portfolio_summary_pie_allocation","Allokaatio")}
                    </div>
                    <Typography variant="h4" className="text-bold mb-0">
                      {t("allocation_market_value","markkina-arvo")}
                    </Typography>
                  </div>
                </div>
                <div className="ml-md-auto flex-shrink-0 mt-2 mt-lg-0">
                  <PieChartTypeSelect name="pie_chart_type" label=" " onChange={setFieldValue} setFieldValue={setFieldValue} value={filters?.pie_chart_type} />
                </div>
            </CardHeader>
            <CardBody className="my-0 py-0">
              <div className="">
                {filters?.pie_chart_type?.value ==3 &&<PortfolioSummaryTypePieChart data={rows ? rows: []}/>}
                {filters?.pie_chart_type?.value ==2 &&<PortfolioSummarySectorPieChart data={rows ? rows: []}/>}
                {filters?.pie_chart_type?.value ==1 &&<PortfolioSummaryPieChart data={rows ? rows: []}/>}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xs={12} xl={4}>
          <Card className="portfolio-summary-chart-card shadow-sm-strong h-100">
            <CardHeader className="sk-frontpage-card-header portfolio-summary-chart-card__header d-flex justify-content-between align-items-start flex-wrap">
                <div className="d-flex align-items-start mr-2 mb-2 mb-lg-0 flex-grow-1 min-w-0">
                  <div className="sk-card-header-icon-box mr-3 flex-shrink-0" aria-hidden>
                    <i className="fas fa-sort-amount-up text-muted" />
                  </div>
                  <div className="min-w-0">
                    <div className="portfolio-summary-chart-card__eyebrow text-muted text-uppercase small font-weight-bold">
                      {t("perfomance_card_title","Nousijat ja laskijat")}
                    </div>
                    <Typography variant="h4" className="text-bold mb-0">
                      {t("portfolio_summary_change_in_period", "Muutos jaksolla")}
                    </Typography>
                  </div>
                </div>
                  <div className="portfolio-summary-segmented align-self-start ml-md-auto flex-shrink-0 mt-2 mt-lg-0">
                    <button
                      type="button"
                      className={`portfolio-summary-segmented__btn ${bestPerfomanceTimePeriod === "month" ? "is-active" : ""}`}
                      onClick={() => setBestPerfomanceTimePeriod("month")}
                    >
                      {t("month","1kk")}
                    </button>
                    <button
                      type="button"
                      className={`portfolio-summary-segmented__btn ${bestPerfomanceTimePeriod === "ytd" ? "is-active" : ""}`}
                      onClick={() => setBestPerfomanceTimePeriod("ytd")}
                    >
                      {t("ytd","YTD")}
                    </button>
                    <button
                      type="button"
                      className={`portfolio-summary-segmented__btn ${bestPerfomanceTimePeriod === "1y" ? "is-active" : ""}`}
                      onClick={() => setBestPerfomanceTimePeriod("1y")}
                    >
                      {t("1y","1v.")}
                    </button>
                    <button
                      type="button"
                      className={`portfolio-summary-segmented__btn ${bestPerfomanceTimePeriod === "all" ? "is-active" : ""}`}
                      onClick={() => setBestPerfomanceTimePeriod("all")}
                    >
                      {t("all_time","Kaikki")}
                    </button>
                  </div>
            </CardHeader>
            <CardBody className="my-0 py-0 d-flex flex-column">
              <div className="mt-2">
                <BestWorstPerformance  
                portfolio_list={chartPortfolioList} 
                date={filters?.date} 
                related_model_type={filters?.investmentType} 
                sectors={selectedSector?.sector} 
                currency={selected_currency || portfolio?.currency_label}
                rows={rows} 
                bestPerfomanceTimePeriod={bestPerfomanceTimePeriod}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      }
      </>
    )
    }

  
  const unrealizedTotal =
    (Number(summary?.market_value) || 0) + (Number(summary?.value_total_summary) || 0);

  const heroPortfolioTitle =
    selectedPortfolios?.selected_portfolios?.length > 1
      ? t("portfolio_summary_n_portfolios_selected", "{{count}} salkkua", {
          count: selectedPortfolios.selected_portfolios.length,
        })
      : selectedPortfolios?.selected_portfolios?.[0]?.name || portfolio?.name;

  const statusDateFormatted = filters?.date ? moment(filters.date).format("DD.MM.YYYY") : "";
  const securitiesTotal = Array.isArray(rows) ? rows.length : 0;

  return (
    <>
      <PageContainer className="portfolio-summary-page">
   <Row className="portfolio-summary-page__toolbar d-flex flex-wrap">
    <Col xs="12">
      <BreadcrumbsDynamic links={getCrumbLinks()} />
    </Col>
    </Row>

    <section className="portfolio-summary-page__hero">
      <div className="portfolio-summary-page__hero-main">
        <div className="portfolio-summary-page__hero-left">
          <div className="portfolio-summary-page__eyebrow">
            {t("portfolios_summary_path","Salkun yhteenveto")}
          </div>
          <h1 className="portfolio-summary-page__title">{heroPortfolioTitle}</h1>
          <p className="portfolio-summary-page__meta">
            {t("portfolio_summary_status_meta", "Tilannepäivä {{date}} · {{count}} arvopaperia", {
              date: statusDateFormatted,
              count: securitiesTotal,
            })}
          </p>
        </div>
        <div className="portfolio-summary-page__hero-right text-md-right mt-3 mt-md-0">
          <div className="portfolio-summary-page__eyebrow">{t("market_value","Markkina-arvo")}</div>
          <div className="portfolio-summary-page__hero-value">
            {thousandFormat(roundScaleDecimals(summary?.market_value ?? 0, 2))} {selected_currency || portfolio?.currency_label}
          </div>
          <div
            className={`portfolio-summary-page__hero-delta ${
              unrealizedTotal >= 0 ? "text-success" : "text-danger"
            }`}
          >
            <i className={`fas fa-arrow-${unrealizedTotal >= 0 ? "up" : "down"} mr-1`} aria-hidden />
            {unrealizedTotal > 0 ? "+" : unrealizedTotal < 0 ? "−" : ""}
            {thousandFormat(roundScaleDecimals(Math.abs(unrealizedTotal), 2))}{" "}
            {t("portfolio_summary_unrealized_label", "epärealisoitu")}
          </div>
        </div>
      </div>
    </section>

        <div className="portfolio-summary-page__totals bg-lighter rounded p-3 mb-3">
      <Typography variant="display5">
      <Row>
        <Col className="col-sm-12 col-md-6 col-xl-6">
          <Row className="align-items-center portfolio-summary-page__totals-row">
            <Col xs="auto" className="portfolio-summary-page__totals-label">{t("portfolios_summary_market_value","Markkina-arvo")}</Col>
            <Col className={summary?.market_value >= 0?"text-success":"text-warning"}>{thousandFormat(roundScaleDecimals(summary?.market_value, 2))} </Col>
          </Row>
          <Row className="align-items-center portfolio-summary-page__totals-row">
            <Col xs="auto" className="portfolio-summary-page__totals-label">{t("portfolios_summary_purchase_costs","Hankintameno")}</Col>
            <Col className={summary?.value_total_summary >= 0?"text-success":"text-warning"}>
              {thousandFormat(roundScaleDecimals(summary?.value_total_summary, 2))}
            </Col>
          </Row>
          {showFairValues && (
            <Row className="align-items-center portfolio-summary-page__totals-row portfolio-summary-page__totals-row--fair-value">
              <Col xs="auto" className="portfolio-summary-page__totals-label">
                {fairValueView === "ifrs"
                  ? t("fair_value_total_ifrs", "Käypä arvo (IFRS)")
                  : t("fair_value_total_live", "Käypä arvo (live)")}
              </Col>
              <Col>
                <span className="portfolio-summary-page__fair-value-total">
                  {fairValueTotal ? formatAmount(fairValueTotal.total) : "–"}
                </span>
                {fairValueTotal?.missing > 0 && (
                  <div className="portfolio-summary-page__fair-value-missing small text-muted">
                    {t("fair_value_missing", "Käypä arvo puuttuu {{n}} arvopaperilta", { n: fairValueTotal.missing })}
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Col>
        <Col className="col-sm-12 col-md-6 col-xl-6">
          <Row className="align-items-center portfolio-summary-page__totals-row">
            <Col xs="auto" className="portfolio-summary-page__totals-label">{t("portfolios_summary_profit","Tuotto")} {selected_currency}</Col>
            <Col className={summary?.market_value + summary?.value_total_summary >= 0?"text-success ":"text-warning"}>
              {thousandFormat(roundScaleDecimals(summary?.market_value + summary?.value_total_summary, 2))}
            </Col>
          </Row>
          <Row className="align-items-center portfolio-summary-page__totals-row">
            <Col xs="auto" className="portfolio-summary-page__totals-label">{t("portfolios_summary_profit", "Tuotto")} %</Col>
            <Col className={(summary?.market_value + summary?.value_total_summary) / (summary?.value_total_summary * -1) < 0 || (summary?.market_value + summary?.value_total_summary) / (summary?.value_total_summary * -1) == null?"text-warning":"text-success"}>
              {thousandFormat(roundScaleDecimals(summary?.market_value == 0 && summary?.value_total_summary == 0? 0:(summary?.market_value + summary?.value_total_summary) / (summary?.value_total_summary * -1) * 100, 2))} %
            </Col>
          </Row>
        </Col>
      </Row>
      </Typography>
        </div>

        {/* {JSON.stringify(portfolioSummary)} */}
        {/* <PortfoliosSummaryTableLineChart portfolio={portfolio.id} date__gte={getFirstDayXMonthsAgo(11)} setChartData={setChartData} /> */}


  
      
        <div className="portfolio-summary-filters-card">
        <div className="portfolio-summary-filters">
          <div className="portfolio-summary-filters__item">
            {getPortfolioSelect()}
          </div>

          <div className="portfolio-summary-filters__item">
            {getInvestmentTypeSelect()}
          </div>

          <div className="portfolio-summary-filters__item">
            {getSectorSelect()}
          </div>

          <div className="portfolio-summary-filters__item">
            {getTagSelect()}
          </div>

          <div className="portfolio-summary-filters__item">
            {getCurrencyDateLogicSelect()}
          </div>

          {
            account?.pif_unlisted_funds_use_own_logic &&
            <div className="portfolio-summary-filters__item">
              <PIFUnlistedFundsSelect name="calculate_type" label={t("portfolio_summary_calculate_type", "Noteeraamaton rahasto")} isMulti={true} placeholder={t('Huomioi myös')} onChange={handleChange} value={filters?.calculate_type} setFilters={setFilters}/>
            </div>
          }
          <div className="portfolio-summary-filters__item">
            {getCurrencySelect()}
          </div>

          <div className="portfolio-summary-filters__item">
            <Input label={t("portfolios_list_date_filter", "Tilannepäivä")} name="date" type="date" value={filters?.date} onChange={handleChange} />
          </div>

          <div className="portfolio-summary-filters__item portfolio-summary-filters__item--actions">
            <Button name="profit_search_button" className="sk-quick-actions__btn portfolio-summary-page__search-btn" variant="secondary" onClick={() => getSummaryData()}>{t("Hae")}</Button>
          </div>
        </div>
        <ReportCurrencyNote selected={selectedPortfolios?.selected_portfolios} currency={selected_currency} className="mt-2" />
        </div>
        {/* {JSON.stringify(filters)} */}
        {get_charts()}
        {showFairValues && (
          <div className="portfolio-summary-fair-value d-flex flex-wrap align-items-center mb-2">
            <span className="portfolio-summary-fair-value__title mr-3">{t("fair_value_title", "Käypä arvo")}</span>
            <div className="portfolio-summary-segmented" role="group" aria-label={t("fair_value_view_label", "Käyvän arvon näkymä")}>
              {FAIR_VALUE_VIEWS.map((view) => (
                <button
                  key={view}
                  type="button"
                  name={`fair_value_view_${view}`}
                  aria-pressed={fairValueView === view}
                  className={`portfolio-summary-segmented__btn ${fairValueView === view ? "is-active" : ""}`}
                  onClick={() => setFairValueView(view)}
                >
                  {view === "ifrs" ? t("fair_value_view_ifrs", "IFRS") : t("fair_value_view_live", "Live")}
                </button>
              ))}
            </div>
            {fairValueError && (
              <span className="text-danger ml-3" role="alert">
                {t("fair_value_error", "Käypiä arvoja ei saatu haettua")}
              </span>
            )}
          </div>
        )}
        {getTable()}
       
        
        <Row>
          <Col>{/*t("Yhteenveto")*/}</Col>
        </Row>
        
        <div className="bg-success">

        </div>
        
        <Modal
          title={t("Salkku")}
          isOpen={modalEditOpen}
          toggleModal={toggleModalEdit}
          classNameBody="p-0 padding-0"
        >
          <PortfolioEdit
            objectId={modalEditId}
            handleResolve={modalEditResolve}
            handleReject={modalEditReject}
          />
        </Modal>
      
      </PageContainer>
      <Typography  variant="small" className="mt-5 text-gray">
        * {t("portfolios_summary_info_text_1","Hankintakurssi on laskettu painoitetulla keskihinnalla")}
      </Typography>

    </>
  );
}

export default PortfolioSummary;
